/** Default segment duration in milliseconds (1 s keeps glass-to-glass under 5 s). */
export const DEFAULT_SEGMENT_DURATION_MS = 1000;

/**
 * Segment duration the fixed latency floors below were originally tuned for (2 s).
 * Floors are scaled linearly by `segmentDurationMs / FLOOR_REFERENCE_SEGMENT_MS` so shorter
 * segments get proportionally shorter waits.
 */
export const FLOOR_REFERENCE_SEGMENT_MS = 2000;

function scaledFloorMs(baseMs: number, segmentDurationMs: number): number {
  return Math.floor((baseMs * segmentDurationMs) / FLOOR_REFERENCE_SEGMENT_MS);
}

/** Maximum time to wait for a P2P segment before falling back to the seed.
 *  This value is now respected for live-edge playback (Fase 1 latency program).
 *  The previous liveEdgeWaitMs(*2) was overriding it in most cases.
 */
export const P2P_DEADLINE_MS = 1500;

/** Maximum number of peer connections per viewer */
export const MAX_PEER_CONNECTIONS = 8;

/** Minimum connected WebRTC peers before playback tries P2P */
export const MIN_SWARM_SIZE_PLAYBACK = 1;

/** Minimum signaling swarm size before prefetch uses P2P */
export const MIN_SWARM_SIZE_PREFETCH = 2;

/** Grace before origin fetch for the elected swarm origin-fetcher (ms) - Fase 1 tuning */
export const LIVE_ORIGIN_LEADER_GRACE_MS = 250;

/** Max wait for P2P-only peers before origin fallback (ms) */
export const LIVE_P2P_ONLY_DEADLINE_MS = 2_000;

/** Grace when racing speculative peers without confirmed holders (ms) - reduced in Fase 1 for lower latency */
export const LIVE_SPECULATIVE_GRACE_MS = 450;

/** Max wait for WebRTC when signaling already reports swarm peers (ms) */
export const LIVE_PEER_CONNECT_WAIT_MS = 600;

/** Max wait before seed fallback on live edge (ms). Scales with segment duration:
 *  1.25 segments, with a floor of 1200 ms at 2 s segments that scales linearly
 *  (600 ms at 1 s). The scheduler p2pDeadlineMs can further tighten the window per-client.
 */
export function liveEdgeWaitMs(segmentDurationMs = DEFAULT_SEGMENT_DURATION_MS): number {
  return Math.max(
    scaledFloorMs(1200, segmentDurationMs),
    Math.floor(segmentDurationMs * 1.25),
  );
}

/**
 * Minimum time a non-fetcher waits for the elected fetcher to pull from the seed
 * and relay before falling back to the seed itself. Floors (1400 ms normal,
 * 800 ms ultra at 2 s segments) scale with segment duration. Always capped by
 * liveEdgeWaitMs in the scheduler.
 */
export function fetcherRelayFloorMs(
  segmentDurationMs = DEFAULT_SEGMENT_DURATION_MS,
  ultraLowLatency = false,
): number {
  return ultraLowLatency
    ? Math.max(Math.floor(segmentDurationMs * 1.0), scaledFloorMs(800, segmentDurationMs))
    : Math.max(Math.floor(segmentDurationMs * 0.75), scaledFloorMs(1400, segmentDurationMs));
}

/**
 * Playback buffer/latency bands — SINGLE SOURCE OF TRUTH.
 *
 * Both the P2P SegmentScheduler (ms) and the player MUST use
 * these so the two never drift. The hard invariant the system depends on:
 *
 *   recoverBufferThresholdMs < offloadBufferThresholdMs < bufferingGoalMs(profile)
 *
 * If offload >= bufferingGoal, the player buffer can never reach the 'offload'
 * band, so speculative-peer P2P + offload-mode prefetch never engage and P2P
 * offload stays ~0. See p2p-offload-latency-constraints in agent memory.
 */
export function recoverBufferThresholdMs(
  segmentDurationMs = DEFAULT_SEGMENT_DURATION_MS,
  ultraLowLatency = false,
): number {
  return ultraLowLatency
    ? Math.max(700, Math.floor(segmentDurationMs * 0.45))
    : Math.max(800, Math.floor(segmentDurationMs * 0.5));
}

export function offloadBufferThresholdMs(
  segmentDurationMs = DEFAULT_SEGMENT_DURATION_MS,
  ultraLowLatency = false,
): number {
  return ultraLowLatency
    ? Math.max(1000, Math.floor(segmentDurationMs * 0.5))
    : Math.max(1800, Math.floor(segmentDurationMs * 0.9));
}

/** Player buffering goal (ms) per latency profile. offloadBufferThresholdMs must stay below this. */
export type LatencyProfile = 'ultra' | 'low' | 'normal';
export function bufferingGoalMs(profile: LatencyProfile): number {
  return profile === 'ultra' ? 1600 : profile === 'low' ? 2500 : 4000;
}

/**
 * Lower offload bar when WebRTC peers are connected so low-latency steady-state
 * buffers (~1.2–1.6s under a 2.5s goal) still enter P2P offload + prefetch.
 * Must stay: recover < swarmOffload < bufferingGoal.
 */
export function swarmOffloadBufferThresholdMs(
  segmentDurationMs = DEFAULT_SEGMENT_DURATION_MS,
  ultraLowLatency = false,
  profile: LatencyProfile = 'low',
): number {
  const recover = recoverBufferThresholdMs(segmentDurationMs, ultraLowLatency);
  const goal = bufferingGoalMs(profile);
  const fromGoal = Math.floor(goal * 0.55);
  return Math.min(
    offloadBufferThresholdMs(segmentDurationMs, ultraLowLatency),
    Math.max(recover + 200, fromGoal),
  );
}

export function targetLatencyMs(
  segmentDurationMs = DEFAULT_SEGMENT_DURATION_MS,
  ultraLowLatency = false,
): number {
  return ultraLowLatency
    ? Math.max(1800, Math.floor(segmentDurationMs * 1.4))
    : Math.max(3000, Math.floor(segmentDurationMs * 1.5));
}

/** Reliability score threshold to consider a peer "good" */
export const GOOD_PEER_THRESHOLD = 0.7;

/** Maximum segments to keep in buffer */
export const MAX_BUFFER_SEGMENTS = 30;

/** STUN server default */
export const DEFAULT_STUN_URL = 'stun:stun.l.google.com:19302';

/** DataChannel names for Fase 2 prioritized transport (urgent for live edge, bulk for prefetch/uploads) */
export const URGENT_DATACHANNEL_NAME = 'omp-urgent';
export const BULK_DATACHANNEL_NAME = 'omp-bulk';
